import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import {
  appointmentApi,
  FilterAppointmentDto,
  CreateAppointmentDto,
  UpdateAppointmentDto
} from '../services/appointmentApi';
import { toast } from 'sonner';
import { extractErrorMessage } from '@/utils/errorHandling';

// Query keys factory
export const appointmentKeys = {
  all: ['appointments'] as const,
  lists: () => [...appointmentKeys.all, 'list'] as const,
  list: (filters?: FilterAppointmentDto) => [...appointmentKeys.lists(), filters] as const,
  details: () => [...appointmentKeys.all, 'detail'] as const,
  detail: (id: string) => [...appointmentKeys.details(), id] as const,
  statistics: () => [...appointmentKeys.all, 'statistics'] as const,
  todayWalkInQueue: () => [...appointmentKeys.all, 'queue', 'today'] as const,
};

/**
 * Hook to fetch paginated appointments list with optional filters
 */
export const useAppointments = (filters?: FilterAppointmentDto) => {
  return useQuery({
    queryKey: appointmentKeys.list(filters),
    queryFn: () => appointmentApi.getAppointments(filters),
    staleTime: 30000, // Consider data fresh for 30 seconds
    // Keep showing the previous day's data while the new day loads so the
    // calendar updates in place instead of flashing a full-page spinner on
    // every date change (isLoading only fires on the first mount now).
    placeholderData: keepPreviousData,
  });
};

/**
 * Hook to fetch the clinic's booking horizon (days ahead bookings are
 * allowed; 0 = unlimited). Defaults to 30 while loading so the date
 * picker never opens unbounded.
 */
export const useBookingHorizon = () => {
  const query = useQuery({
    queryKey: [...appointmentKeys.all, 'booking-horizon'] as const,
    queryFn: () => appointmentApi.getBookingHorizon(),
    staleTime: 10 * 60 * 1000, // rarely changes
  });
  return { ...query, horizonDays: query.data?.data?.days ?? 30 };
};

/**
 * Hook to fetch a single appointment by ID
 */
export const useAppointment = (id: string) => {
  return useQuery({
    queryKey: appointmentKeys.detail(id),
    queryFn: () => appointmentApi.getAppointmentById(id),
    enabled: !!id, // Only fetch if ID is provided
    staleTime: 30000,
  });
};

/**
 * Hook to fetch appointment statistics
 */
export const useAppointmentStatistics = () => {
  return useQuery({
    queryKey: appointmentKeys.statistics(),
    queryFn: () => appointmentApi.getAppointmentStatistics(),
    staleTime: 60000, // Statistics can be cached longer
  });
};

/**
 * Hook to create a new appointment
 */
export const useCreateAppointment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (appointmentData: CreateAppointmentDto) => appointmentApi.createAppointment(appointmentData),
    onSuccess: () => {
      // Invalidate and refetch appointments list
      queryClient.invalidateQueries({ queryKey: appointmentKeys.lists() });
      queryClient.invalidateQueries({ queryKey: appointmentKeys.statistics() });
      toast.success('Appointment created successfully');
    },
    onError: (error) => {
      toast.error(extractErrorMessage(error) || 'Failed to create appointment');
    },
  });
};

/**
 * Hook to update an existing appointment
 */
export const useUpdateAppointment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, appointmentData }: { id: string; appointmentData: UpdateAppointmentDto }) =>
      appointmentApi.updateAppointment(id, appointmentData),
    onSuccess: (_, { id }) => {
      // Invalidate specific appointment and lists
      queryClient.invalidateQueries({ queryKey: appointmentKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: appointmentKeys.lists() });
      queryClient.invalidateQueries({ queryKey: appointmentKeys.statistics() });
      toast.success('Appointment updated successfully');
    },
    onError: (error) => {
      toast.error(extractErrorMessage(error) || 'Failed to update appointment');
    },
  });
};

/**
 * Receptionist / provider marks a walk-in patient as physically
 * present. Invalidates appointment details + the queue feed so
 * every open queue UI sees the new position immediately.
 */
export const useCheckInAppointment = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => appointmentApi.checkInAppointment(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: appointmentKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: appointmentKeys.lists() });
      queryClient.invalidateQueries({
        queryKey: appointmentKeys.todayWalkInQueue(),
      });
      toast.success('Patient checked in');
    },
    onError: (error) => {
      toast.error(extractErrorMessage(error) || 'Could not check the patient in');
    },
  });
};

/**
 * Live snapshot of today's walk-in queue. Polled every 15s so the
 * receptionist card stays fresh even without an explicit refresh.
 */
export const useTodayWalkInQueue = () => {
  return useQuery({
    queryKey: appointmentKeys.todayWalkInQueue(),
    queryFn: () => appointmentApi.getTodayWalkInQueue(),
    staleTime: 15_000,
    refetchInterval: 15_000,
  });
};

/**
 * Hook to delete an appointment
 */
export const useDeleteAppointment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => appointmentApi.deleteAppointment(id),
    onSuccess: () => {
      // Invalidate lists and statistics
      queryClient.invalidateQueries({ queryKey: appointmentKeys.lists() });
      queryClient.invalidateQueries({ queryKey: appointmentKeys.statistics() });
      toast.success('Appointment deleted successfully');
    },
    onError: (error) => {
      toast.error(extractErrorMessage(error) || 'Failed to delete appointment');
    },
  });
};
